import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Activity, Pill, AlertTriangle,
  Syringe, Heart, Calendar, User, Loader2,
  ChevronDown, ChevronUp, Download, Stethoscope,
  Thermometer, Clock, FileDown, Eye, Printer
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  parseTreatmentPlan,
  generatePrescriptionHTML,
  openPrescriptionWindow,
} from '../lib/prescriptionTemplates';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function PatientMedicalHistory() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState(null);
  const [doctors, setDoctors] = useState({});
  const [expandedConsultation, setExpandedConsultation] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  useEffect(() => {
    fetchPatientData();
  }, []);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const patientId = user?.patient_id;

      if (!patientId) {
        toast.error('No se encontró información del paciente');
        setLoading(false);
        return;
      }

      const [patientRes, consultationsRes, historyRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/consultations/patient/${patientId}`),
        api.get(`/medical-history/${patientId}`).catch(() => null)
      ]);

      setPatientData(patientRes.data);
      setConsultations(consultationsRes.data || []);
      setMedicalHistory(historyRes?.data || null);

      // Fetch doctor names for each consultation
      const uniqueDoctorIds = [...new Set(consultationsRes.data?.map(c => c.doctor_id) || [])];
      const doctorPromises = uniqueDoctorIds.map(id =>
        api.get(`/doctors/${id}`).catch(() => null)
      );
      const doctorResponses = await Promise.all(doctorPromises);
      const doctorMap = {};
      doctorResponses.forEach((res, idx) => {
        if (res?.data) {
          doctorMap[uniqueDoctorIds[idx]] = res.data;
        }
      });
      setDoctors(doctorMap);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Error al cargar la información médica');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setDetailDialogOpen(true);
  };

  /**
   * Genera la receta con la misma plantilla que ve el médico.
   * El branding y el formato salen del doctor que atendió la consulta, no del
   * paciente, para que el documento sea idéntico al que emitió el consultorio.
   */
  const generatePrescriptionContent = (consultation) => {
    return generatePrescriptionHTML({
      consultation,
      patient: patientData,
      branding: doctors[consultation.doctor_id],
    });
  };

  /**
   * Abre la receta en una ventana nueva.
   *
   * @param {Object} consultation - Consulta de la que se emite la receta
   * @param {boolean} autoPrint - true imprime directo, false muestra el botón "Guardar PDF"
   */
  const openPrescription = (consultation, autoPrint) => {
    const opened = openPrescriptionWindow(generatePrescriptionContent(consultation), {
      autoPrint,
      accentColor: doctors[consultation.doctor_id]?.primary_color || '#2563eb',
    });

    if (!opened) {
      toast.error('El navegador bloqueó la ventana emergente. Permite las ventanas emergentes para este sitio.');
    }
  };

  // Imprimir receta médica
  const handlePrintPrescription = (consultation) => openPrescription(consultation, true);

  // Descargar receta médica como PDF
  const handleDownloadPrescription = (consultation) => openPrescription(consultation, false);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="space-y-6">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Mi Historia Clínica
        </h1>
        <Card className="p-6">
          <p className="text-muted-foreground text-center py-8">
            No se encontró información del paciente. Por favor contacte al administrador.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="medical-history-title">
          Mi Historia Clínica
        </h1>
        <p className="text-muted-foreground mt-2">
          Consulta tu información médica y historial de consultas
        </p>
      </div>

      {/* Datos Personales */}
      <Card className="p-6" data-testid="personal-info-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Datos Personales
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Nombre Completo</p>
            <p className="font-medium">{patientData.first_name} {patientData.last_name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">DNI</p>
            <p className="font-medium">{patientData.dni}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Fecha de Nacimiento</p>
            <p className="font-medium">{formatDate(patientData.birth_date)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Género</p>
            <p className="font-medium capitalize">{patientData.gender}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Teléfono</p>
            <p className="font-medium">{patientData.phone || 'No registrado'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Email</p>
            <p className="font-medium">{patientData.email || 'No registrado'}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-muted-foreground">Dirección</p>
            <p className="font-medium">{patientData.address || 'No registrada'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Seguro</p>
            <p className="font-medium">{patientData.insurance || 'No registrado'}</p>
          </div>
        </div>
      </Card>

      {/* Antecedentes y Alergias */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="pathological-history-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Antecedentes Patológicos
            </h2>
          </div>
          {patientData.antecedentes_patologicos && Object.keys(patientData.antecedentes_patologicos).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(patientData.antecedentes_patologicos).map(([key, value]) => (
                value && (
                  <div key={key} className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-red-500" />
                    <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                  </div>
                )
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin antecedentes patológicos registrados</p>
          )}
        </Card>

        <Card className="p-6" data-testid="allergies-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Alergias
            </h2>
          </div>
          {patientData.alergias_detalle && patientData.alergias_detalle.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patientData.alergias_detalle.map((alergia, index) => (
                <span key={index} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                  {alergia}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin alergias registradas</p>
          )}
        </Card>

        <Card className="p-6" data-testid="current-medication-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Medicación Actual
            </h2>
          </div>
          {patientData.medicacion_actual && patientData.medicacion_actual.length > 0 ? (
            <div className="space-y-2">
              {patientData.medicacion_actual.map((med, index) => (
                <div key={index} className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-sm">{med.nombre || med.name}</p>
                  <p className="text-xs text-muted-foreground">{med.dosis || med.dosage} - {med.frecuencia || med.frequency}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin medicación registrada</p>
          )}
        </Card>

        <Card className="p-6" data-testid="immunizations-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
              <Syringe className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Inmunizaciones
            </h2>
          </div>
          {patientData.inmunizaciones && patientData.inmunizaciones.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patientData.inmunizaciones.map((inmunizacion, index) => (
                <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  {inmunizacion}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin inmunizaciones registradas</p>
          )}
        </Card>
      </div>

      {/* Historial de Consultas - Rediseñado */}
      <Card className="p-6" data-testid="consultations-history-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Historial de Consultas
            </h2>
            <p className="text-sm text-muted-foreground">
              {consultations.length} consulta{consultations.length !== 1 ? 's' : ''} registrada{consultations.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {consultations.length > 0 ? (
          <div className="space-y-4">
            {consultations.map((consultation) => {
              const doctor = doctors[consultation.doctor_id];
              const plan = parseTreatmentPlan(consultation.treatment_plan);
              const hasMedications = plan?.medicamentos && plan.medicamentos.length > 0;

              return (
                <Card key={consultation.id} className="border-2 overflow-hidden hover:border-purple-200 transition-colors">
                  {/* Header de la consulta */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-purple-700">
                          <Calendar className="w-5 h-5" />
                          <span className="font-semibold">
                            {formatFullDate(consultation.consultation_date)}
                          </span>
                        </div>
                        {doctor && (
                          <div className="flex items-center gap-2 text-blue-700">
                            <Stethoscope className="w-4 h-4" />
                            <span className="text-sm">
                              Dr(a). {doctor.full_name}
                              {doctor.specialization && (
                                <span className="text-blue-500 ml-1">({doctor.specialization})</span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(consultation)}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Detalle
                        </Button>
                        {hasMedications && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPrescription(consultation)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              title="Descargar Receta PDF"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Descargar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintPrescription(consultation)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              title="Imprimir Receta"
                            >
                              <Printer className="w-4 h-4 mr-1" />
                              Imprimir
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contenido principal */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Motivo de consulta */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Motivo de Consulta</p>
                        <p className="text-sm bg-gray-50 p-3 rounded-lg">{consultation.reason}</p>
                      </div>

                      {/* Diagnóstico */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Diagnóstico</p>
                        <p className="text-sm bg-blue-50 p-3 rounded-lg text-blue-800 font-medium">
                          {consultation.diagnosis || 'No especificado'}
                        </p>
                      </div>
                    </div>

                    {/* Signos Vitales */}
                    {consultation.vital_signs && Object.values(consultation.vital_signs).some(v => v) && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Signos Vitales</p>
                        <div className="flex flex-wrap gap-2">
                          {consultation.vital_signs.blood_pressure && (
                            <span className="px-3 py-1.5 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                              PA: {consultation.vital_signs.blood_pressure}
                            </span>
                          )}
                          {consultation.vital_signs.heart_rate && (
                            <span className="px-3 py-1.5 bg-pink-50 text-pink-700 rounded-full text-xs font-medium">
                              FC: {consultation.vital_signs.heart_rate} lpm
                            </span>
                          )}
                          {consultation.vital_signs.temperature && (
                            <span className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                              T°: {consultation.vital_signs.temperature}°C
                            </span>
                          )}
                          {consultation.vital_signs.respiratory_rate && (
                            <span className="px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium">
                              FR: {consultation.vital_signs.respiratory_rate} rpm
                            </span>
                          )}
                          {consultation.vital_signs.oxygen_saturation && (
                            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                              SatO2: {consultation.vital_signs.oxygen_saturation}%
                            </span>
                          )}
                          {consultation.vital_signs.weight && (
                            <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                              Peso: {consultation.vital_signs.weight} kg
                            </span>
                          )}
                          {consultation.vital_signs.height && (
                            <span className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                              Talla: {consultation.vital_signs.height} cm
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Medicamentos prescritos */}
                    {hasMedications && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Medicamentos Prescritos ({plan.medicamentos.length})
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {plan.medicamentos.map((med, idx) => (
                            <div key={idx} className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                              <p className="font-semibold text-green-800">{med.nombre}</p>
                              <p className="text-xs text-green-600 mt-1">
                                {med.dosis} • {med.frecuencia} • {med.duracion} • Vía {med.via}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Indicaciones */}
                    {consultation.indications && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Indicaciones</p>
                        <p className="text-sm bg-amber-50 p-3 rounded-lg text-amber-800 border-l-4 border-amber-400">
                          {consultation.indications}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">No hay consultas registradas</p>
            <p className="text-sm text-gray-400 mt-1">Las consultas médicas aparecerán aquí</p>
          </div>
        )}
      </Card>

      {/* Dialog de detalle completo */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Detalle de Consulta Médica
            </DialogTitle>
          </DialogHeader>

          {selectedConsultation && (() => {
            const doctor = doctors[selectedConsultation.doctor_id];
            const plan = parseTreatmentPlan(selectedConsultation.treatment_plan);

            return (
              <div className="space-y-6">
                {/* Info header */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Fecha de Consulta</p>
                    <p className="font-semibold text-purple-800">{formatFullDate(selectedConsultation.consultation_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Médico Tratante</p>
                    <p className="font-semibold text-blue-800">
                      Dr(a). {doctor?.full_name || 'No especificado'}
                      {doctor?.specialization && <span className="font-normal text-blue-600"> - {doctor.specialization}</span>}
                    </p>
                  </div>
                </div>

                {/* Motivo */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Motivo de Consulta</h3>
                  <p className="p-3 bg-gray-50 rounded-lg">{selectedConsultation.reason}</p>
                </div>

                {/* Signos Vitales */}
                {selectedConsultation.vital_signs && Object.values(selectedConsultation.vital_signs).some(v => v) && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Signos Vitales</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedConsultation.vital_signs.blood_pressure && (
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <p className="text-xs text-red-600 uppercase">Presión Arterial</p>
                          <p className="text-lg font-bold text-red-800">{selectedConsultation.vital_signs.blood_pressure}</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.heart_rate && (
                        <div className="p-3 bg-pink-50 rounded-lg text-center">
                          <p className="text-xs text-pink-600 uppercase">Frec. Cardíaca</p>
                          <p className="text-lg font-bold text-pink-800">{selectedConsultation.vital_signs.heart_rate} lpm</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.temperature && (
                        <div className="p-3 bg-orange-50 rounded-lg text-center">
                          <p className="text-xs text-orange-600 uppercase">Temperatura</p>
                          <p className="text-lg font-bold text-orange-800">{selectedConsultation.vital_signs.temperature}°C</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.respiratory_rate && (
                        <div className="p-3 bg-cyan-50 rounded-lg text-center">
                          <p className="text-xs text-cyan-600 uppercase">Frec. Respiratoria</p>
                          <p className="text-lg font-bold text-cyan-800">{selectedConsultation.vital_signs.respiratory_rate} rpm</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.oxygen_saturation && (
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-xs text-blue-600 uppercase">Sat. Oxígeno</p>
                          <p className="text-lg font-bold text-blue-800">{selectedConsultation.vital_signs.oxygen_saturation}%</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.weight && (
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-green-600 uppercase">Peso</p>
                          <p className="text-lg font-bold text-green-800">{selectedConsultation.vital_signs.weight} kg</p>
                        </div>
                      )}
                      {selectedConsultation.vital_signs.height && (
                        <div className="p-3 bg-purple-50 rounded-lg text-center">
                          <p className="text-xs text-purple-600 uppercase">Talla</p>
                          <p className="text-lg font-bold text-purple-800">{selectedConsultation.vital_signs.height} cm</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Evaluación Clínica */}
                {selectedConsultation.clinical_evaluation && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Evaluación Clínica</h3>
                    <p className="p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">{selectedConsultation.clinical_evaluation}</p>
                  </div>
                )}

                {/* Diagnóstico */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Diagnóstico</h3>
                  <p className="p-3 bg-blue-50 rounded-lg text-blue-800 font-medium border-l-4 border-blue-500">
                    {selectedConsultation.diagnosis || 'No especificado'}
                  </p>
                </div>

                {/* Medicamentos */}
                {plan?.medicamentos && plan.medicamentos.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Prescripción Médica</h3>
                    <div className="space-y-2">
                      {plan.medicamentos.map((med, idx) => (
                        <div key={idx} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-green-800 text-lg">{med.nombre}</p>
                            <span className="px-2 py-1 bg-green-200 text-green-800 rounded text-xs uppercase">{med.via}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mt-3">
                            <div>
                              <p className="text-xs text-green-600 uppercase">Dosis</p>
                              <p className="font-medium">{med.dosis}</p>
                            </div>
                            <div>
                              <p className="text-xs text-green-600 uppercase">Frecuencia</p>
                              <p className="font-medium">{med.frecuencia}</p>
                            </div>
                            <div>
                              <p className="text-xs text-green-600 uppercase">Duración</p>
                              <p className="font-medium">{med.duracion}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nutrición */}
                {plan?.nutricion && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Indicaciones Nutricionales</h3>
                    <p className="p-3 bg-lime-50 rounded-lg text-lime-800 border-l-4 border-lime-500">{plan.nutricion}</p>
                  </div>
                )}

                {/* Laboratorio */}
                {plan?.laboratorio && plan.laboratorio.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Exámenes de Laboratorio Solicitados</h3>
                    <div className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                      <ul className="list-disc list-inside text-purple-800 space-y-1">
                        {plan.laboratorio.map((exam, i) => (
                          <li key={i}>{exam}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Imágenes */}
                {plan?.imagenes && plan.imagenes.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Estudios de Imagen Solicitados</h3>
                    <div className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                      <ul className="list-disc list-inside text-orange-800 space-y-1">
                        {plan.imagenes.map((img, i) => (
                          <li key={i}>{img}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Interconsultas */}
                {plan?.interconsultas && plan.interconsultas.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Interconsultas Solicitadas</h3>
                    <div className="p-3 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                      <ul className="list-disc list-inside text-teal-800 space-y-1">
                        {plan.interconsultas.map((esp, i) => (
                          <li key={i}>{esp}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Indicaciones */}
                {selectedConsultation.indications && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Indicaciones Adicionales</h3>
                    <p className="p-3 bg-amber-50 rounded-lg text-amber-800 border-l-4 border-amber-500 whitespace-pre-wrap">
                      {selectedConsultation.indications}
                    </p>
                  </div>
                )}

                {/* Botones de descarga e impresión */}
                {plan?.medicamentos && plan.medicamentos.length > 0 && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadPrescription(selectedConsultation)}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Receta PDF
                    </Button>
                    <Button
                      onClick={() => handlePrintPrescription(selectedConsultation)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir Receta
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
